import { Modal, Box, TextField, Autocomplete, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useState, useEffect } from "react";
import useGetOrganizationProposalChecklistItems from "hooks/organization/useGetOrganizationProposalChecklistItems";
import TableSkeleton from "components/TableSkeleton/TableSkeleton";
import MDAlert from "components/MDAlert";
import CreateChecklistItemModal from "./CreateChecklistItemModal";

interface AddStageChecklistModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (selectedItems: string[], notes: string) => void;
  existingItems?: string[];
  existingNotes?: string;
  isSaving?: boolean;
}

export default function AddStageChecklistModal({
  open,
  onClose,
  onSave,
  existingItems = [],
  existingNotes = "",
  isSaving = false,
}: AddStageChecklistModalProps) {
  const { data: checklistItems, loading, error } = useGetOrganizationProposalChecklistItems();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [notes, setNotes] = useState(existingNotes);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedItems(existingItems);
      setNotes(existingNotes);
    }
  }, [open, existingItems, existingNotes]);

  const handleSave = () => {
    onSave(selectedItems, notes);
    onClose();
  };

  const handleCreateNewItem = () => {
    setCreateModalOpen(true);
  };

  const handleCreateModalClose = () => {
    setCreateModalOpen(false);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="modal-checklist-title"
        aria-describedby="modal-checklist-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            maxWidth: "90%",
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <MDTypography id="modal-checklist-title" variant="h6" mb={3}>
            Job requirements
          </MDTypography>

          <MDBox id="modal-checklist-description" mb={3}>
            {loading && <TableSkeleton numberOfLines={5} />}
            {error && <MDAlert color="error">{error.message}</MDAlert>}
            {!loading && !error && checklistItems.length === 0 && (
              <MDTypography variant="body2" color="text">
                No checklist items available.
              </MDTypography>
            )}
            {!loading && !error && checklistItems.length > 0 && (
              <Autocomplete
                multiple
                id="checklist-items-select"
                options={[
                  { id: "create-new", name: "Create new item", isCreateOption: true },
                  ...[...checklistItems].sort((a, b) => a.name.localeCompare(b.name)),
                ]}
                value={checklistItems.filter((item) => selectedItems.includes(item.id))}
                getOptionLabel={(option) => option.name}
                disableCloseOnSelect
                onChange={(event, newValue) => {
                  const createOption = newValue.find((item) => item.isCreateOption);
                  if (createOption) {
                    handleCreateNewItem();
                    return;
                  }
                  setSelectedItems(newValue.map((item) => item.id));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Select Checklist Items"
                    placeholder="Select items..."
                  />
                )}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      ...(option.isCreateOption && {
                        backgroundColor: "action.hover",
                        borderRadius: 1,
                        fontWeight: "medium",
                        "&:hover": {
                          backgroundColor: "action.selected",
                        },
                      }),
                    }}
                  >
                    {option.isCreateOption && <AddIcon sx={{ mr: 1, fontSize: 20 }} />}
                    {option.name}
                  </Box>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option.name} {...getTagProps({ index })} key={option.id} />
                  ))
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}
          </MDBox>

          <MDBox mb={3}>
            <MDTypography variant="body" mb={1}>
              Notes
            </MDTypography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Add any additional notes here..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              variant="outlined"
            />
          </MDBox>

          <MDBox display="flex" justifyContent="flex-end" gap={2}>
            <MDButton onClick={onClose} color="secondary" variant="outlined" disabled={isSaving}>
              Cancel
            </MDButton>
            <MDButton
              onClick={handleSave}
              color="success"
              variant="contained"
              disabled={loading || isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </MDButton>
          </MDBox>
        </Box>
      </Modal>

      <CreateChecklistItemModal open={createModalOpen} onClose={handleCreateModalClose} />
    </>
  );
}
